/**
 * Componente: TimelineContent
 *
 * Renderiza a timeline unificada de um cliente
 * Mostra: Agendamentos → Conversão → Contratos em ordem cronológica
 */

import React, { useMemo, useState } from 'react';
import { Loader2, AlertCircle, Calendar, CheckCircle, FileText, Copy, Mail, Phone, X, Layers, CreditCard, Wallet, ChevronDown, GitBranch, ArrowRightLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useClientJourney, TimelineEvent } from '../hooks/useClientJourney';
import { groupEventsIntoCycles, getCycleStats, type ClientCycle } from '../utils/clientCycles';
import { usePermissao } from '@/features/auth/hooks/usePermissao';
import { formatDate, formatDateUTC3, formatDateTimeUTC3 } from '@/lib/utils';
import { formatStatusLabel } from '@/lib/statusLabels';
import { toast } from 'sonner';

const formatCPF = (cpf: string) => {
  if (!cpf) return '';
  const clean = cpf.replace(/\D/g, '');
  return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatPhone = (phone: string) => {
  if (!phone) return '';
  const clean = phone.replace(/\D/g, '');
  if (clean.length === 10) {
    return clean.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  if (clean.length === 11) {
    return clean.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  return phone;
};

const translateStatus = formatStatusLabel;

const STATUS_CONFIG: Record<
  string,
  { label: string; bg: string; text: string; dot: string }
> = {
  contemplado: {
    label: "Contemplado",
    bg: "bg-amber-500/10 dark:bg-amber-500/10",
    text: "text-amber-600 dark:text-amber-400",
    dot: "bg-amber-500",
  },
  ativo: {
    label: "Ativo",
    bg: "bg-teal-500/10 dark:bg-teal-500/10",
    text: "text-teal-700 dark:text-teal-400",
    dot: "bg-teal-500",
  },
  finalizado: {
    label: "Finalizado",
    bg: "bg-teal-500/10 dark:bg-teal-500/10",
    text: "text-teal-700 dark:text-teal-400",
    dot: "bg-teal-500",
  },
  assinada: {
    label: "Assinado",
    bg: "bg-teal-500/10 dark:bg-teal-500/10",
    text: "text-teal-700 dark:text-teal-400",
    dot: "bg-teal-500",
  },
  pendente: {
    label: "Pendente",
    bg: "bg-blue-500/10 dark:bg-blue-500/10",
    text: "text-blue-700 dark:text-blue-400",
    dot: "bg-blue-500",
  },
  em_andamento: {
    label: "Em Andamento",
    bg: "bg-blue-500/10 dark:bg-blue-500/10",
    text: "text-blue-700 dark:text-blue-400",
    dot: "bg-blue-500",
  },
  aguardando_entrada: {
    label: "Aguardando Entrada",
    bg: "bg-blue-500/10 dark:bg-blue-500/10",
    text: "text-blue-700 dark:text-blue-400",
    dot: "bg-blue-500",
  },
  cancelado: {
    label: "Cancelado",
    bg: "bg-red-500/10 dark:bg-red-500/10",
    text: "text-red-700 dark:text-red-400",
    dot: "bg-red-500",
  },
  rejeitado: {
    label: "Rejeitado",
    bg: "bg-red-500/10 dark:bg-red-500/10",
    text: "text-red-700 dark:text-red-400",
    dot: "bg-red-500",
  },
};

const getStatusCfg = (status: string) =>
  STATUS_CONFIG[String(status).toLowerCase()] ?? {
    label: formatStatusLabel(status),
    bg: "bg-zinc-500/10",
    text: "text-zinc-500 dark:text-zinc-400",
    dot: "bg-zinc-400",
  };

interface TimelineContentProps {
  clienteId: string;
  onEventClick?: (event: TimelineEvent) => void;
  /** Layout compacto para painel lateral do modal de pipeline */
  variant?: 'full' | 'embedded';
  /** Agendamento vinculado ao card atual — destaca o ciclo de negócio correspondente */
  highlightAgendamentoId?: number;
  /** Card do pipeline atual — quando informado, inclui as movimentações de stage na timeline */
  cardId?: string;
  /** Quando informado, o CTA "Novo Contrato" chama isso em vez de navegar para /contratos/novo */
  onGoToContratos?: () => void;
}

export function TimelineContent({
  clienteId,
  onEventClick,
  variant = 'full',
  highlightAgendamentoId,
  cardId,
  onGoToContratos,
}: TimelineContentProps) {
  const { journey, loading, error } = useClientJourney(clienteId, cardId);
  const [selectedContrato, setSelectedContrato] = useState<any>(null);
  const isEmbedded = variant === 'embedded';

  const cycles = useMemo(
    () =>
      journey?.events
        ? groupEventsIntoCycles(journey.events, highlightAgendamentoId)
        : [],
    [journey?.events, highlightAgendamentoId]
  );

  const currentCycle = cycles.find((c) => c.isCurrent);
  const otherCycles = cycles.filter((c) => !c.isCurrent);

  const handleEventClick = (event: TimelineEvent) => {
    if (event.type === 'CONTRATO' && !onEventClick) {
      setSelectedContrato(event.data);
    }
    onEventClick?.(event);
  };

  if (loading) {
    return <TimelineLoading />;
  }

  if (error) {
    return <TimelineError error={error} />;
  }

  if (!journey) {
    return <TimelineEmpty />;
  }

  if (isEmbedded) {
    return (
      <div className="space-y-4">
        <div className="px-1">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {journey.cliente.nome}
          </h4>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            {cycles.length > 1
              ? `${cycles.length} ciclos de negócio · destaque no ciclo deste lead`
              : `${journey.stats.total_agendamentos} agend. · ${journey.stats.total_contratos} contratos`}
          </p>
        </div>

        {currentCycle ? (
          <>
            <TimelineCycleStats cycle={currentCycle} isCurrent />
            <TimelineCycleSection
              cycle={currentCycle}
              isCurrent
              onEventClick={handleEventClick}
            />
          </>
        ) : cycles.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 dark:border-zinc-700 p-4 text-center">
            <GitBranch className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Nenhum evento na jornada ainda. Salve o agendamento para iniciar o ciclo.
            </p>
          </div>
        ) : null}

        {otherCycles.length > 0 && (
          <TimelineOtherCycles cycles={otherCycles} onEventClick={handleEventClick} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TimelineHeader journey={journey} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna principal */}
        <div className="lg:col-span-2 space-y-6">
          <TimelineStats journey={journey} />
          <TimelineEvents events={journey.events} onEventClick={handleEventClick} />
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <TimelineActionsSidebar journey={journey} onGoToContratos={onGoToContratos} />
        </div>
      </div>

      {/* Modal de detalhes do contrato */}
      {selectedContrato && (
        <ContratoModal
          contrato={selectedContrato}
          onClose={() => setSelectedContrato(null)}
        />
      )}
    </div>
  );
}

/**
 * Header com informações do cliente
 */
function TimelineHeader({ journey }: { journey: any }) {
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  };

  return (
    <div className="rounded-[20px] bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-3xl font-semibold text-gray-900 dark:text-white mb-1">
            {journey.cliente.nome}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Cliente desde {formatDate(journey.cliente.created_at)}
          </p>
        </div>
        <span className="text-xs font-semibold px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg">
          {translateStatus(journey.cliente.status)}
        </span>
      </div>

      {/* Contatos */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {journey.cliente.cpf && (
          <div
            className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-zinc-800/50 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-800 transition"
            onClick={() => copyToClipboard(journey.cliente.cpf, 'CPF')}
          >
            <span className="text-gray-400">ID</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">CPF</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {formatCPF(journey.cliente.cpf)}
              </p>
            </div>
          </div>
        )}

        {journey.cliente.email && (
          <div
            className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-zinc-800/50 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-800 transition"
            onClick={() => copyToClipboard(journey.cliente.email, 'Email')}
          >
            <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {journey.cliente.email}
              </p>
            </div>
          </div>
        )}

        {journey.cliente.telefone && (
          <div
            className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-zinc-800/50 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-800 transition"
            onClick={() => copyToClipboard(journey.cliente.telefone, 'Telefone')}
          >
            <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">Telefone</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {formatPhone(journey.cliente.telefone)}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Estatísticas de um ciclo específico
 */
function TimelineCycleStats({
  cycle,
  isCurrent,
}: {
  cycle: ClientCycle;
  isCurrent?: boolean;
}) {
  const stats = getCycleStats(cycle);

  return (
    <div
      className={`grid grid-cols-3 gap-2 rounded-lg p-2 ${
        isCurrent
          ? 'bg-[#FF3737]/5 border border-[#FF3737]/20'
          : 'bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800'
      }`}
    >
      <div className="text-center">
        <p className="text-[10px] text-gray-500">Agend.</p>
        <p className="text-sm font-bold text-gray-900 dark:text-white">{stats.agendamentos}</p>
      </div>
      <div className="text-center">
        <p className="text-[10px] text-gray-500">Contratos</p>
        <p className="text-sm font-bold text-gray-900 dark:text-white">{stats.contratos}</p>
      </div>
      <div className="text-center">
        <p className="text-[10px] text-gray-500">Última</p>
        <p className="text-[11px] font-semibold text-gray-900 dark:text-white truncate">
          {stats.ultima ?? '—'}
        </p>
      </div>
    </div>
  );
}

/**
 * Bloco de um ciclo de negócio
 */
function TimelineCycleSection({
  cycle,
  isCurrent,
  onEventClick,
}: {
  cycle: ClientCycle;
  isCurrent?: boolean;
  onEventClick?: (event: TimelineEvent) => void;
}) {
  return (
    <section
      className={`rounded-xl overflow-hidden ${
        isCurrent
          ? 'ring-2 ring-[#FF3737]/30 border border-[#FF3737]/25 bg-[#FF3737]/[0.03] dark:bg-[#FF3737]/5'
          : 'border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900'
      }`}
    >
      <div
        className={`px-3 py-2 flex items-center gap-2 border-b ${
          isCurrent
            ? 'border-[#FF3737]/20 bg-[#FF3737]/10'
            : 'border-gray-100 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-800/50'
        }`}
      >
        <GitBranch
          className={`w-3.5 h-3.5 flex-shrink-0 ${isCurrent ? 'text-[#FF3737]' : 'text-gray-400'}`}
        />
        <div className="flex-1 min-w-0">
          {isCurrent && (
            <span className="text-[10px] font-bold uppercase tracking-wide text-[#FF3737] block">
              Atendimento deste lead
            </span>
          )}
          <p className="text-[11px] font-medium text-gray-700 dark:text-gray-300 truncate">
            {cycle.label}
          </p>
        </div>
      </div>
      <div className="p-3">
        {cycle.events.length > 0 ? (
          <TimelineEvents events={cycle.events} onEventClick={onEventClick} compact />
        ) : (
          <p className="text-xs text-gray-400 text-center py-4">Nenhum evento neste atendimento</p>
        )}
      </div>
    </section>
  );
}

/**
 * Ciclos anteriores — colapsáveis
 */
function TimelineOtherCycles({
  cycles,
  onEventClick,
}: {
  cycles: ClientCycle[];
  onEventClick?: (event: TimelineEvent) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
      >
        <span className="uppercase tracking-wide">
          Outros atendimentos ({cycles.length})
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {expanded && (
        <div className="space-y-3 opacity-80">
          {cycles.map((cycle) => (
            <TimelineCycleSection
              key={cycle.id}
              cycle={cycle}
              onEventClick={onEventClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Estatísticas resumidas
 */
function TimelineStats({ journey, compact = false }: { journey: any; compact?: boolean }) {
  if (compact) {
    return (
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-2 text-center">
          <p className="text-[10px] text-gray-500">Agend.</p>
          <p className="text-sm font-bold text-gray-900 dark:text-white">
            {journey.stats.total_agendamentos}
          </p>
        </div>
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-2 text-center">
          <p className="text-[10px] text-gray-500">Contratos</p>
          <p className="text-sm font-bold text-gray-900 dark:text-white">
            {journey.stats.total_contratos}
          </p>
        </div>
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-2 text-center">
          <p className="text-[10px] text-gray-500">Última</p>
          <p className="text-[11px] font-semibold text-gray-900 dark:text-white truncate">
            {journey.stats.ultima_interacao ? formatDateUTC3(journey.stats.ultima_interacao) : '—'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-[14px] p-4">
        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
          Agendamentos
        </p>
        <p className="text-2xl font-semibold text-gray-900 dark:text-white">
          {journey.stats.total_agendamentos}
        </p>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-[14px] p-4">
        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
          Contratos
        </p>
        <p className="text-2xl font-semibold text-gray-900 dark:text-white">
          {journey.stats.total_contratos}
        </p>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-[14px] p-4">
        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
          Última Interação
        </p>
        <p className="text-sm font-semibold text-gray-900 dark:text-white">
          {journey.stats.ultima_interacao ? formatDateUTC3(journey.stats.ultima_interacao) : '—'}
        </p>
      </div>
    </div>
  );
}

/**
 * Timeline dos eventos
 */
function TimelineEvents({
  events,
  onEventClick,
  compact = false,
}: {
  events: TimelineEvent[];
  onEventClick?: (event: TimelineEvent) => void;
  compact?: boolean;
}) {
  if (events.length === 0) {
    return (
      <div className={`bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 ${compact ? 'rounded-lg p-4' : 'rounded-[20px] p-8'} text-center`}>
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          Nenhum evento na timeline ainda
        </p>
      </div>
    );
  }

  return (
    <div className={compact ? 'space-y-0' : 'space-y-0'}>
      {events.map((event, index) => (
        <TimelineEventItem
          key={event.id}
          event={event}
          compact={compact}
          isLast={index === events.length - 1}
          onClick={() => onEventClick?.(event)}
        />
      ))}
    </div>
  );
}

function formatEventDisplayDate(event: TimelineEvent): string {
  if (event.type === 'AGENDAMENTO') {
    const raw = event.data?.data_agendamento;
    if (raw) return formatDateUTC3(String(raw));
  }
  if (event.type === 'CONTRATO') {
    return formatDateTimeUTC3(event.data?.created_at ?? event.timestamp);
  }
  return formatDateTimeUTC3(event.timestamp);
}

/**
 * Item individual da timeline
 */
function TimelineEventItem({
  event,
  compact = false,
  isLast = false,
  onClick,
}: {
  event: TimelineEvent;
  compact?: boolean;
  isLast?: boolean;
  onClick: () => void;
}) {
  const getEventIcon = (type: string) => {
    switch (type) {
      case 'AGENDAMENTO':
        return <Calendar className="h-3.5 w-3.5" />;
      case 'CONVERSAO':
        return <CheckCircle className="h-3.5 w-3.5" />;
      case 'CONTRATO':
        return <FileText className="h-3.5 w-3.5" />;
      case 'STATUS_CHANGE':
        return <ArrowRightLeft className="h-3.5 w-3.5" />;
      default:
        return <div className="h-3.5 w-3.5 rounded-full bg-gray-300 dark:bg-gray-600" />;
    }
  };

  const getBgColor = (type: string) => {
    switch (type) {
      case 'AGENDAMENTO':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
      case 'CONVERSAO':
        return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
      case 'CONTRATO':
        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      case 'STATUS_CHANGE':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';
      default:
        return 'bg-gray-100 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className={`flex items-stretch gap-3 ${compact ? 'pb-3' : 'pb-6'}`}>
      {/* Coluna do ícone + linha vertical */}
      <div className="flex flex-col items-center flex-shrink-0 w-8">
        <div
          className={`w-7 h-7 rounded-full border-2 border-white dark:border-zinc-900 flex items-center justify-center flex-shrink-0 z-10 ${getBgColor(event.type)}`}
        >
          {getEventIcon(event.type)}
        </div>
        {!isLast && (
          <div className="w-px flex-1 min-h-3 bg-gray-200 dark:bg-zinc-700 mt-1" />
        )}
      </div>

      {/* Card do evento */}
      <div
        className={`flex-1 min-w-0 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 cursor-pointer hover:border-gray-300 dark:hover:border-zinc-700 transition ${
          compact ? 'rounded-lg p-3' : 'rounded-[12px] p-4'
        }`}
        onClick={onClick}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate min-w-0">
            {event.title}
          </h3>
          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
            {formatEventDisplayDate(event)}
          </span>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400">
          {event.type === 'CONTRATO' ? translateStatus(event.description) : event.description}
        </p>

        {event.actorName && (
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
            Por {event.actorName}
          </p>
        )}

        {event.type === 'CONTRATO' && (event.data?.valor_bem || event.data?.valor_contrato) && (
          <div className="text-xs text-gray-500 dark:text-gray-500 mt-3 pt-3 border-t border-gray-100 dark:border-zinc-800">
            R$ {Number(event.data?.valor_bem || event.data?.valor_contrato).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Sidebar com Próximas Ações
 */
function TimelineActionsSidebar({
  journey,
  onGoToContratos,
}: {
  journey: any;
  onGoToContratos?: () => void;
}) {
  const navigate = useNavigate();
  const { podeCriarContrato } = usePermissao();

  const actions = [];

  if (journey.stats.total_contratos === 0 && podeCriarContrato) {
    actions.push({
      label: 'Novo Contrato',
      onClick: () =>
        onGoToContratos
          ? onGoToContratos()
          : navigate(
              `/contratos/novo?cliente_id=${journey.cliente.id}&cliente_nome=${encodeURIComponent(journey.cliente.nome)}`
            )
    });
  }

  if (journey.events.length > 0) {
    const lastEvent = journey.events[0];
    const daysAgo = Math.floor(
      (new Date().getTime() - new Date(lastEvent.timestamp).getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysAgo > 7) {
      actions.push({
        label: `Follow-up (${daysAgo}d)`
      });
    }
  }

  return (
    <div className="space-y-4">
      {/* Ações */}
      {actions.length > 0 && (
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-[20px] p-6">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Ações
          </h3>
          <div className="space-y-2">
            {actions.map((action, i) => (
              <button
                key={i}
                onClick={action.onClick}
                className="w-full text-left px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-zinc-800 hover:bg-gray-100 dark:hover:bg-zinc-700 rounded-lg transition"
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Info Card */}
      <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-[20px] p-6">
        <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-4 uppercase tracking-wide">
          Resumo
        </h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Agendamentos</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {journey.stats.total_agendamentos}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Contratos</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {journey.stats.total_contratos}
            </span>
          </div>
          <div className="flex justify-between pt-3 border-t border-gray-100 dark:border-zinc-800">
            <span className="text-gray-600 dark:text-gray-400">Status</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {translateStatus(journey.cliente.status)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Estados de carregamento e erro
 */
function TimelineLoading() {
  return (
    <div className="flex flex-col items-center justify-center h-96 gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Carregando...
      </p>
    </div>
  );
}

function TimelineError({ error }: { error: Error }) {
  return (
    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-[20px] p-6 flex gap-4">
      <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
      <div>
        <h3 className="text-sm font-medium text-red-900 dark:text-red-200 mb-1">
          Erro ao carregar
        </h3>
        <p className="text-sm text-red-800 dark:text-red-300">
          {error.message}
        </p>
      </div>
    </div>
  );
}

function TimelineEmpty() {
  return (
    <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-[20px] p-12 text-center">
      <p className="text-gray-600 dark:text-gray-400">
        Nenhum evento encontrado
      </p>
    </div>
  );
}

/**
 * Stat Card component para o modal
 */
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  accent?: string;
}

function StatCard({ icon, label, value, accent = "bg-teal-500/10" }: StatCardProps) {
  return (
    <div className="rounded-[12px] bg-gray-50 dark:bg-zinc-800/50 border border-gray-100 dark:border-zinc-700 p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className={`p-2 ${accent} rounded-lg`}>{icon}</div>
        <span className="text-[0.65rem] font-bold uppercase tracking-widest text-gray-600 dark:text-gray-400">
          {label}
        </span>
      </div>
      <div className="text-lg font-semibold text-gray-900 dark:text-white">
        {value}
      </div>
    </div>
  );
}

/**
 * Modal com detalhes completos do contrato
 */
function ContratoModal({ contrato, onClose }: { contrato: any; onClose: () => void }) {
  const navigate = useNavigate();

  if (!contrato) return null;

  const statusCfg = getStatusCfg(contrato.status || '');

  const nomeCliente =
    contrato.cliente?.nome ||
    contrato.clientes?.nome ||
    'Cliente não identificado';

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatCpfCnpj = (value: string) => {
    if (!value) return 'N/A';
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11)
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (digits.length === 14)
      return digits.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        '$1.$2.$3/$4-$5'
      );
    return value;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-zinc-900 rounded-[20px] max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-zinc-800 shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-zinc-800">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[0.6rem] font-bold uppercase tracking-widest text-gray-600 dark:text-gray-400">
                Contrato
              </span>
              <span className="text-[0.65rem] font-extrabold px-2 py-0.5 rounded-full bg-teal-500/10 text-teal-700 dark:text-teal-400 border border-teal-500/20">
                #{contrato.contrato_numero || 'N/A'}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {nomeCliente}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg transition"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {/* Status Badge */}
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${statusCfg.bg} ${statusCfg.text} border-current/20 uppercase tracking-widest`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${statusCfg.dot}`} />
              {statusCfg.label}
            </span>
          </div>

          {/* Stat Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              icon={<Layers className="h-4 w-4 text-teal-600 dark:text-teal-400" />}
              label="Status"
              accent="bg-teal-500/10"
              value={
                <span className={`text-sm font-extrabold uppercase tracking-wide ${statusCfg.text}`}>
                  {statusCfg.label}
                </span>
              }
            />
            <StatCard
              icon={<CreditCard className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />}
              label="Valor do Bem"
              accent="bg-indigo-500/10"
              value={formatCurrency(
                Number(contrato.bens?.preco_bem || contrato.bem?.preco_bem || contrato.valor_bem || 0)
              )}
            />
            <StatCard
              icon={<Wallet className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />}
              label="Parcelas Pagas"
              accent="bg-emerald-500/10"
              value={
                <span>
                  {contrato.parcelas_pagas || 0}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    {' '} / 18
                  </span>
                </span>
              }
            />
            <StatCard
              icon={<Calendar className="h-4 w-4 text-violet-600 dark:text-violet-400" />}
              label="Próxima Assembleia"
              accent="bg-violet-500/10"
              value={
                <span className="text-base">
                  {formatDate(contrato.data_assembleia)}
                </span>
              }
            />
          </div>

          {/* Dados do Beneficiário */}
          <div className="border-t border-gray-200 dark:border-zinc-800 pt-6">
            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4">
              Dados do Beneficiário
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Nome Completo
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {nomeCliente}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    CPF/CNPJ
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {formatCpfCnpj(
                      contrato.cliente?.cpf || contrato.clientes?.cpf || ''
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Email
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {contrato.cliente?.email || contrato.clientes?.email || 'N/A'}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Contatos
                </p>
                <div className="flex flex-wrap gap-2">
                  {(contrato.cliente?.tel1 || contrato.clientes?.tel1) && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 dark:bg-zinc-800 text-sm text-gray-900 dark:text-white">
                      <Phone className="h-3 w-3" />
                      {formatPhone(contrato.cliente?.tel1 || contrato.clientes?.tel1)}
                    </span>
                  )}
                  {(contrato.cliente?.tel2 || contrato.clientes?.tel2) && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 dark:bg-zinc-800 text-sm text-gray-900 dark:text-white">
                      <Phone className="h-3 w-3" />
                      {formatPhone(contrato.cliente?.tel2 || contrato.clientes?.tel2)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bem & Grupo */}
          <div className="border-t border-gray-200 dark:border-zinc-800 pt-6">
            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4">
              Bem & Grupo
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Tipo de Bem
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {contrato.bens?.tipo_bem || contrato.bem?.tipo_bem || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Preço do Bem
                </p>
                <p className="text-sm text-gray-900 dark:text-white font-medium">
                  {formatCurrency(
                    Number(contrato.bens?.preco_bem || contrato.bem?.preco_bem || contrato.valor_bem || 0)
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Grupo
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {contrato.grupos?.numero_grupo || contrato.grupo?.numero_grupo || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Cota
                </p>
                <p className="text-sm text-gray-900 dark:text-white font-medium">
                  {contrato.cota || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Vendedor
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {contrato.consultor?.nome || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Data Contrato
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {formatDate(contrato.created_at)}
                </p>
              </div>
            </div>
          </div>

          {/* Bem Detalhes */}
          {contrato.bens && (contrato.bens.marca || contrato.bens.modelo) && (
            <div className="border-t border-gray-200 dark:border-zinc-800 pt-6">
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4">
                Detalhes do Bem
              </h3>
              <div className="bg-gray-50 dark:bg-zinc-800/50 rounded-lg p-4 space-y-2">
                {contrato.bens.marca && (
                  <p className="text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Marca: </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {contrato.bens.marca}
                    </span>
                  </p>
                )}
                {contrato.bens.modelo && (
                  <p className="text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Modelo: </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {contrato.bens.modelo}
                    </span>
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-800/50">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-800 transition font-medium text-sm"
          >
            Fechar
          </button>
          <button
            onClick={() => {
              navigate(`/contratos/${contrato.id}`);
              onClose();
            }}
            className="flex-1 px-4 py-2 text-white bg-teal-600 dark:bg-teal-700 rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 transition font-medium text-sm"
          >
            Ver Detalhes Completos
          </button>
        </div>
      </div>
    </div>
  );
}
